'use client';

import { memo, useId, useState, type ComponentProps } from 'react';
import Markdown, { type Components, type ExtraProps } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

const previewRows = 10;

function AnswerTable({ children, node }: ComponentProps<'table'> & ExtraProps) {
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  const body = node?.children.find(child => child.type === 'element' && child.tagName === 'tbody');
  const rowCount = body?.type === 'element'
    ? body.children.filter(child => child.type === 'element' && child.tagName === 'tr').length
    : 0;
  const collapsible = rowCount > previewRows;

  return (
    <div className="my-4 min-w-0 overflow-hidden rounded-md border border-[var(--color-border)]">
      <div
        id={id}
        role="region"
        aria-label="Answer table"
        tabIndex={0}
        className="overflow-x-auto focus-visible:outline-2 focus-visible:outline-[var(--color-focus)] focus-visible:-outline-offset-2"
      >
        <table className={'w-full border-collapse text-sm ' + (!expanded && collapsible ? '[&_tbody>tr:nth-child(n+11)]:hidden' : '')}>
          {children}
        </table>
      </div>
      {collapsible && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-[var(--color-border)] px-4 py-2">
          <span className="text-xs text-[var(--color-foreground-muted)]">
            Showing {expanded ? rowCount : previewRows} of {rowCount} rows in this answer
          </span>
          <Button variant="ghost" type="button" aria-expanded={expanded} aria-controls={id} onClick={() => setExpanded(value => !value)}>
            {expanded ? <ChevronUp aria-hidden="true" /> : <ChevronDown aria-hidden="true" />}
            {expanded ? 'Show fewer' : `Show all ${rowCount} rows`}
          </Button>
        </div>
      )}
    </div>
  );
}

const components: Components = {
  h1: ({ children }) => <h3 className="mb-2 mt-5 text-lg font-semibold first:mt-0">{children}</h3>,
  h2: ({ children }) => <h3 className="mb-2 mt-5 text-base font-semibold first:mt-0">{children}</h3>,
  h3: ({ children }) => <h4 className="mb-2 mt-4 font-semibold first:mt-0">{children}</h4>,
  h4: ({ children }) => <h4 className="mb-2 mt-4 font-semibold first:mt-0">{children}</h4>,
  h5: ({ children }) => <h4 className="mb-2 mt-4 font-semibold first:mt-0">{children}</h4>,
  h6: ({ children }) => <h4 className="mb-2 mt-4 font-semibold first:mt-0">{children}</h4>,
  p: ({ children }) => <p className="my-3 whitespace-pre-wrap first:mt-0 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="my-3 list-disc space-y-1 pl-6">{children}</ul>,
  ol: ({ children, start }) => <ol start={start} className="my-3 list-decimal space-y-1 pl-6">{children}</ol>,
  blockquote: ({ children }) => <blockquote className="my-3 border-l-2 border-[var(--color-border)] pl-4 text-[var(--color-foreground-muted)]">{children}</blockquote>,
  pre: ({ children }) => <pre className="my-3 overflow-x-auto rounded-md bg-[var(--color-surface-muted)] p-3 text-sm">{children}</pre>,
  code: ({ children }) => <code className="rounded bg-[var(--color-surface-muted)] px-1 font-mono text-sm">{children}</code>,
  hr: () => <hr className="my-4 border-[var(--color-border)]" />,
  table: AnswerTable,
  thead: ({ children }) => <thead className="bg-[var(--color-surface-muted)]">{children}</thead>,
  th: ({ children, style }) => <th scope="col" style={style} className="border-b border-[var(--color-border)] px-4 py-3 text-left font-semibold">{children}</th>,
  td: ({ children, style }) => <td style={style} className="border-b border-[var(--color-border)] px-4 py-3 align-top">{children}</td>,
  // Only the separately verified source references are navigable. Model output
  // cannot load remote images or introduce HTML, scripts or interactive controls.
  a: ({ children }) => <>{children}</>,
  img: () => null,
};

const plugins = [remarkGfm];

export const AssistantAnswer = memo(function AssistantAnswer({ content }: { content: string }) {
  return (
    <div className="min-w-0 max-w-full break-words leading-relaxed">
      <Markdown remarkPlugins={plugins} components={components} skipHtml disallowedElements={['input']}>
        {content}
      </Markdown>
    </div>
  );
});
